import { Button } from "@/components/ui/UIElements/Button";
import { PageHeader } from "@finitless/shared/components";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { NativeSelect } from "../../ui/UIElements/Select";
import Pagination from "../../ui/Data/Pagination";
import CampaignsLoading from "../../ui/Campaigns/CampaignsLoading";
import CampaignsError from "../../ui/Campaigns/CampaignsError";
import EmailConfigurationForm from "../../ui/Campaigns/EmailConfigurationForm";
import { useCampaigns } from "../../../hooks/useCampaigns";
import { useIsSmtpConfigured } from "../../../hooks/useEmailConfig";
import { useUrlStates } from "@finitless/shared/hooks";
import {
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGNS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  getContactTypeBadge,
  hasDetailedStats,
  getTimeAgo,
} from "../../../constants/campaignConstants";
import "./campaigns-ordering.css";

const CampaignsPage = () => {
  const { t } = useTranslation(["campaigns", "agentTranslations"]);
  const navigate = useNavigate();
  const { isConfigured: isSmtpConfigured, isLoading: isSmtpLoading } =
    useIsSmtpConfigured();
  const [showConfigForm, setShowConfigForm] = useState(false);

  const initialUrlStates = useMemo(
    () => ({
      contactType: "all",
      status: "all",
      searchTerm: "",
      currentPage: "1",
    }),
    [],
  );

  const [urlStates, updateUrlState] = useUrlStates(initialUrlStates);

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const searchInputRef = useRef(null);
  const [contactType] = useState(urlStates.contactType || "all");
  const [statusFilter, setStatusFilter] = useState(urlStates.status || "all");
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      updateUrlState("searchTerm", searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, updateUrlState]);

  const { data, isLoading, error, refetch } = useCampaigns({
    contactType,
    status: statusFilter,
    searchTerm,
    page: currentPage,
    pageSize: CAMPAIGNS_PER_PAGE,
  });

  const campaigns = data?.campaigns || [];
  const pagination = data?.pagination || {};

  const calculatedTotalPages = pagination?.total
    ? Math.ceil(pagination.total / CAMPAIGNS_PER_PAGE)
    : 1;

  const totalPages = pagination?.total_pages || calculatedTotalPages;

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
    updateUrlState("status", newStatus);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
    searchInputRef.current?.focus();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (campaign) => {
    navigate(`/campaigns/${campaign.id}`, { state: { campaign } });
  };

  const handleCreateCampaign = () => {
    navigate("/campaigns/new");
  };

  const totalSent = campaigns.reduce((acc, c) => acc + (c.sent_count || 0), 0);
  const totalOpens = campaigns.reduce((acc, c) => acc + (c.open_count || 0), 0);
  const totalClicks = campaigns.reduce(
    (acc, c) => acc + (c.clicked_count || 0),
    0,
  );
  const avgOpenRate =
    totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : "0.0";
  const avgClickRate =
    totalSent > 0 ? ((totalClicks / totalSent) * 100).toFixed(1) : "0.0";

  const getStatusOptionLabel = (option) => {
    const labelByValue = {
      all: t("filters.status.all"),
      draft: t("filters.status.draft"),
      ended: t("filters.status.sent"),
      scheduled: t("filters.status.scheduled"),
      pending: t("filters.status.active"),
    };

    return labelByValue[option.value] || option.label;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      ended: t("status.sent"),
      pending: t("status.active"),
      scheduled: t("status.scheduled"),
      draft: t("status.draft"),
    };

    if (!status) {
      return t("status.unknown");
    }

    return (
      statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  if (isSmtpLoading) {
    return <CampaignsLoading />;
  }

  if (!isSmtpConfigured || showConfigForm) {
    return (
      <EmailConfigurationForm
        onConfigured={() => setShowConfigForm(false)}
        onCancel={isSmtpConfigured ? () => setShowConfigForm(false) : null}
      />
    );
  }

  return (
    <div
      className="ordering-campaigns-surface space-y-6"
      data-ordering-expression="product"
    >
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowConfigForm(true)}
            className="border-border bg-card text-muted-foreground hover:bg-card flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm transition-colors"
            title={t("actions.emailSettings")}
          >
            <Icon name="Settings" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("actions.emailSettings")}
            </span>
          </Button>
          <Button
            variant="default"
            type="button"
            onClick={handleCreateCampaign}
            className="flex items-center space-x-2 px-3 py-2 transition-colors sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.newCampaign")}</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Sent */}
        <div className="border-border bg-background flex items-center justify-between rounded-xl border p-5">
          <div>
            <p className="text-muted-foreground mb-1 text-xs tracking-wider uppercase">
              {t("stats.totalSent")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-foreground text-2xl font-bold">
                {totalSent >= 1000
                  ? `${(totalSent / 1000).toFixed(1)}k`
                  : totalSent}
              </span>
            </div>
          </div>
          <div className="bg-accent/20 flex h-10 w-10 items-center justify-center rounded-lg">
            <Icon name="Send" className="text-primary h-5 w-5" />
          </div>
        </div>

        {/* Avg. Open Rate */}
        <div className="border-border bg-background flex items-center justify-between rounded-xl border p-5">
          <div>
            <p className="text-muted-foreground mb-1 text-xs tracking-wider uppercase">
              {t("stats.avgOpenRate")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-foreground text-2xl font-bold">
                {avgOpenRate}%
              </span>
            </div>
          </div>
          <div className="bg-accent/20 flex h-10 w-10 items-center justify-center rounded-lg">
            <Icon name="Mail" className="text-primary h-5 w-5" />
          </div>
        </div>

        {/* Avg. Click Rate */}
        <div className="border-border bg-background flex items-center justify-between rounded-xl border p-5">
          <div>
            <p className="text-muted-foreground mb-1 text-xs tracking-wider uppercase">
              {t("stats.avgClickRate")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-foreground text-2xl font-bold">
                {avgClickRate}%
              </span>
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--success-bg)]">
            <Icon name="MousePointerClick" className="text-success h-5 w-5" />
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="border-border bg-background rounded-xl border p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          {/* Search */}
          <div className="min-w-0 flex-1">
            <div className="ordering-campaigns-search relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-4 w-4" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                aria-label={t("search.placeholder")}
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border-border bg-card placeholder:text-muted-foreground focus:ring-ring text-foreground h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 focus:border-transparent focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  aria-label={t("actions.clearSearch", {
                    ns: "agentTranslations",
                  })}
                  className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform items-center justify-center rounded-full transition-colors"
                  type="button"
                >
                  <Icon name="X" className="text-muted-foreground h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Button */}
          <NativeSelect
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="custom-select border-border bg-card focus:ring-ring text-foreground h-10 rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
          >
            {CAMPAIGN_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {getStatusOptionLabel(option)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {/* Campaigns Table */}
      {isLoading ? (
        <CampaignsLoading />
      ) : error ? (
        <CampaignsError error={error} onRetry={refetch} />
      ) : campaigns.length > 0 ? (
        <>
          {/* Campaigns Table */}
          <div className="border-border bg-background overflow-x-auto rounded-xl border">
            <table
              className="w-full min-w-[700px]"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "30%" }} />
              </colgroup>
              <thead>
                <tr className="border-border border-b">
                  <th className="text-muted-foreground px-4 py-4 text-left text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.campaignName")}
                  </th>
                  <th className="text-muted-foreground px-4 py-4 text-left text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.type")}
                  </th>
                  <th className="text-muted-foreground px-4 py-4 text-center text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.status")}
                  </th>
                  <th className="text-muted-foreground px-6 py-4 text-center text-xs font-medium tracking-wider whitespace-nowrap uppercase">
                    {t("table.columns.performance")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/50 divide-y">
                {campaigns.map((campaign) => {
                  const contactTypeBadge = getContactTypeBadge(
                    campaign.contact_type,
                  );
                  const showStats = hasDetailedStats(campaign.contact_type);
                  const sentCount = campaign.sent_count || 0;
                  const openCount = campaign.open_count || 0;
                  const clickCount = campaign.clicked_count || 0;
                  const openRate =
                    sentCount > 0
                      ? ((openCount / sentCount) * 100).toFixed(1)
                      : "0.0";
                  const clickRate =
                    sentCount > 0
                      ? ((clickCount / sentCount) * 100).toFixed(1)
                      : "0.0";
                  const createdTimeAgo = getTimeAgo(campaign.created_at, {
                    prefix: t("table.createdPrefix"),
                  });

                  return (
                    <tr
                      key={campaign.id}
                      onClick={() => handleViewDetails(campaign)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleViewDetails(campaign);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={campaign.name || t("table.fallback.untitled")}
                      className="hover:bg-card/50 cursor-pointer transition-colors focus-visible:outline-none"
                    >
                      {/* Campaign Name */}
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="text-foreground truncate text-sm font-semibold sm:text-base"
                          title={campaign.name || t("table.fallback.untitled")}
                        >
                          {campaign.name || t("table.fallback.untitled")}
                        </div>
                        <div className="text-muted-foreground mt-1 flex items-center space-x-1 text-xs sm:text-sm">
                          <Icon
                            name="Clock"
                            className="h-3 w-3 flex-shrink-0"
                          />
                          <span>{createdTimeAgo}</span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-4 sm:px-6">
                        <div className="flex items-center space-x-2">
                          <Icon
                            name={contactTypeBadge.icon}
                            className={`h-4 w-4 flex-shrink-0 ${contactTypeBadge.text}`}
                          />
                          <span className="text-muted-foreground text-sm capitalize sm:text-base">
                            {campaign.contact_type || t("table.fallback.email")}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 text-center sm:px-6">
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap ${
                            campaign.status === "ended"
                              ? "border-success/40 text-success bg-[var(--success-bg)]"
                              : campaign.status === "scheduled"
                                ? "border-primary/40 bg-accent/10 text-primary"
                                : campaign.status === "draft"
                                  ? "border-border/40 bg-muted/10 text-muted-foreground"
                                  : "border-warning/40 text-warning bg-[var(--warning-bg)]"
                          }`}
                        >
                          {getStatusLabel(campaign.status)}
                        </span>
                      </td>

                      {/* Performance (Open Rate / CTR) */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <div className="px-4 text-center">
                            <div className="text-foreground font-semibold whitespace-nowrap">
                              {showStats ? `${openRate}%` : "0.0%"}
                            </div>
                            <div className="text-muted-foreground text-xs whitespace-nowrap uppercase">
                              {t("table.metrics.openRate")}
                            </div>
                          </div>
                          <div className="border-border h-8 border-r"></div>
                          <div className="px-4 text-center">
                            <div className="text-foreground font-semibold whitespace-nowrap">
                              {showStats ? `${clickRate}%` : "0.0%"}
                            </div>
                            <div className="text-muted-foreground text-xs whitespace-nowrap uppercase">
                              {t("table.metrics.ctr")}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-border bg-background sticky bottom-0 z-[var(--layer-sticky)] mt-4 flex flex-col items-center justify-between gap-4 rounded-xl border p-4 shadow-[var(--od-shadow-2)] sm:flex-row">
              <div className="text-muted-foreground text-sm">
                {t("pagination.showing", {
                  from: (currentPage - 1) * CAMPAIGNS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * CAMPAIGNS_PER_PAGE,
                    pagination?.total || 0,
                  ),
                  total: pagination?.total || 0,
                })}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
              />
            </div>
          )}
        </>
      ) : (
        <div className="border-border bg-background rounded-xl border p-12 text-center">
          <div className="bg-accent/20 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <Icon name="Megaphone" className="text-primary h-8 w-8" />
          </div>
          <h3 className="text-foreground mb-2 text-lg font-medium">
            {t("empty.title")}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault")}
          </p>
          <Button
            variant="default"
            type="button"
            onClick={handleCreateCampaign}
            className="inline-flex items-center space-x-2 px-4 py-2 transition-colors"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.createCampaign")}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default CampaignsPage;
